/**
 * Typed API client for the App Marketplace (epic 204).
 *
 * Tenants browse a cross-tenant catalogue of published OAuth apps, install
 * (granting scopes) and uninstall them. Tenant admins can submit their own
 * OAuth clients for publication; platform admins review and decide on the
 * pending publication queue.
 *
 * All routes are mounted under `/api/v1/marketplace` and reached through the
 * shared `apiClient` (which attaches the user's Bearer token).
 */

import { apiClient } from '@/lib/apiClient';

/** A published app available for any authed tenant to install. */
export interface AppRow {
  id: string;
  name: string;
  clientId: string;
  logoUrl: string | null;
  homepageUrl: string | null;
  privacyPolicyUrl: string | null;
  allowedScopes: string[];
}

/** An app installed in the current tenant (tenant-scoped). */
export interface InstalledAppRow {
  installId: string;
  oauthClientId: string;
  appName: string;
  clientId: string;
  logoUrl: string | null;
  grantedScopes: string[];
  installedAt: string;
}

/** A pending publication review (PLATFORM_ADMIN only). */
export interface ReviewRow {
  id: string;
  oauthClientId: string;
  appName: string;
  clientId: string;
  submittedAt: string;
  status: string;
  notes: string | null;
}

/** Catalogue of published apps — any authed user. */
export async function listMarketplaceApps(): Promise<AppRow[]> {
  const { data } = await apiClient.get<AppRow[]>('/marketplace/apps');
  return data;
}

/** Apps installed in the current tenant — any authed user. */
export async function listInstalledApps(): Promise<InstalledAppRow[]> {
  const { data } = await apiClient.get<InstalledAppRow[]>('/marketplace/installs');
  return data;
}

/** Install an app into the current tenant, granting the given scopes. TENANT_ADMIN. */
export async function installApp(
  oauthClientId: string,
  grantedScopes: string[],
): Promise<InstalledAppRow> {
  const { data } = await apiClient.post<InstalledAppRow>('/marketplace/installs', {
    oauthClientId,
    grantedScopes,
  });
  return data;
}

/** Uninstall an app from the current tenant. TENANT_ADMIN. */
export async function uninstallApp(installId: string): Promise<void> {
  await apiClient.delete(`/marketplace/installs/${installId}`);
}

/** Submit one of the tenant's own OAuth clients for marketplace publication. TENANT_ADMIN. */
export async function submitPublication(
  oauthClientId: string,
): Promise<{ reviewId: string; status: string }> {
  const { data } = await apiClient.post<{ reviewId: string; status: string }>(
    '/marketplace/publications/submit',
    { oauthClientId },
  );
  return data;
}

/** The pending publication-review queue. PLATFORM_ADMIN only. */
export async function listPendingPublications(): Promise<ReviewRow[]> {
  const { data } = await apiClient.get<ReviewRow[]>('/marketplace/publications/pending');
  return data;
}

/** Approve or reject a pending publication. PLATFORM_ADMIN only. */
export async function decidePublication(
  reviewId: string,
  approved: boolean,
  notes?: string | null,
): Promise<void> {
  await apiClient.post(`/marketplace/publications/${reviewId}/decision`, {
    approved,
    notes: notes ?? null,
  });
}
